/**
 * Transactions Dual-Write Test
 *
 * Tests that transaction operations in Firestore are properly synced to Supabase.
 *
 * Test variations:
 * T1: Create income transaction (standalone)
 * T2: Create expense transaction (standalone)
 * T3: Delete transaction
 */

import { saveNewTransaction, deleteTransaction } from '../global/Firebase/firebase';
import { api } from '../global/API/apiClient';
import { getDefaultStore } from 'jotai';
import { organizationAtom, storesDictAtom, dbAtom } from '../global/AtomsProvider';

// Test variation definitions
export const TRANSACTION_TEST_VARIATIONS = [
  { id: 'T1', category: 'create', name: 'Create income transaction' },
  { id: 'T2', category: 'create', name: 'Create expense transaction' },
  { id: 'T3', category: 'delete', name: 'Delete transaction' },
];

// Get test categories for UI grouping
export function getTransactionTestCategories() {
  return [
    { id: 'create', name: 'Create Transactions', variations: ['T1', 'T2'] },
    { id: 'delete', name: 'Delete Transaction', variations: ['T3'] },
  ];
}

// Helper to normalize values for comparison
function normalizeValue(val) {
  if (val === undefined || val === null) return null;
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    // Try to parse as number
    const num = parseFloat(val);
    if (!isNaN(num)) return num;
    return val.trim();
  }
  return val;
}

// Helper to compare values with tolerance for floating point
function valuesMatch(a, b, tolerance = 0.01) {
  const normA = normalizeValue(a);
  const normB = normalizeValue(b);

  if (normA === normB) return true;
  if (normA === null || normB === null) return normA === normB;

  // Numeric comparison with tolerance
  if (typeof normA === 'number' && typeof normB === 'number') {
    return Math.abs(normA - normB) < tolerance;
  }

  return normA === normB;
}

/**
 * Get test data based on atoms
 */
export function getTransactionTestData() {
  const jotaiStore = getDefaultStore();
  const organization = jotaiStore.get(organizationAtom);
  const storesDict = jotaiStore.get(storesDictAtom);
  const db = jotaiStore.get(dbAtom);

  if (!organization || !db) {
    return null;
  }

  // Get first available store
  const storeEntries = Object.entries(storesDict || {});
  const firstStore = storeEntries[0]?.[1];
  const storeId = firstStore?.id || storeEntries[0]?.[0];

  // Get a wallet ID for the store (typically store-cash)
  const walletId = storeId ? `${storeId}-cash` : null;

  return {
    organization,
    storeId,
    storeName: firstStore?.name || 'Unknown Store',
    walletId,
    db,
    organizationId: organization.id,
  };
}

/**
 * Compare transaction via backend API
 */
async function compareViaApi(transactionId, transactionData, firestoreData) {
  try {
    const response = await api.get(`/transactions/compare/${transactionId}`);
    const apiResult = response.data;

    if (!apiResult.exists) {
      return {
        transactionId,
        foundInSupabase: false,
        foundInFirestore: !!firestoreData,
        matches: false,
        differences: [{
          field: 'transaction',
          supabaseValue: null,
          firestoreValue: 'exists',
          severity: 'critical',
        }],
        warnings: [],
      };
    }

    const supabaseData = apiResult.supabaseData;
    const differences = [];

    // Compare key fields
    const fieldsToCompare = [
      { fs: 'type', sb: 'type' },
      { fs: 'value', sb: 'value' },
      { fs: 'sourceWallet', sb: 'source_wallet' },
      { fs: 'destinationWallet', sb: 'destination_wallet' },
    ];

    for (const { fs, sb } of fieldsToCompare) {
      const fsVal = normalizeValue(firestoreData?.[fs]);
      const sbVal = normalizeValue(supabaseData?.[sb]);

      if (!valuesMatch(fsVal, sbVal)) {
        differences.push({
          field: sb,
          supabaseValue: sbVal,
          firestoreValue: fsVal,
          severity: 'critical',
        });
      }
    }

    return {
      transactionId,
      foundInSupabase: true,
      foundInFirestore: !!firestoreData,
      matches: differences.length === 0,
      differences,
      warnings: [],
      supabaseData,
      firestoreData,
    };
  } catch (error) {
    console.warn('API comparison failed:', error.message);
    return {
      transactionId,
      foundInSupabase: false,
      foundInFirestore: !!firestoreData,
      matches: false,
      differences: [{
        field: 'api_error',
        supabaseValue: null,
        firestoreValue: error.message,
        severity: 'critical',
      }],
      warnings: [`API error: ${error.message}`],
    };
  }
}

/**
 * Check if transaction was deleted from Supabase
 */
async function checkDeleted(transactionId, maxRetries = 3) {
  for (let i = 0; i < maxRetries; i++) {
    try {
      const response = await api.get(`/transactions/compare/${transactionId}`);
      if (!response.data.exists) {
        return true;
      }
      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, 2000));
    } catch (error) {
      // 404 means deleted
      if (error.response?.status === 404) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Run a single transaction test
 */
async function runSingleTest(variationId, testData, log) {
  const { db, organizationId, storeId, walletId } = testData;
  const timestamp = Date.now();

  let createdTransactionRef = null;
  let transactionData = null;
  let result = {
    variationId,
    passed: false,
    error: null,
    comparisonResult: null,
  };

  try {
    switch (variationId) {
      case 'T1': {
        // Create income transaction
        transactionData = {
          type: 'income',
          value: 1000 + (timestamp % 1000),
          sourceWallet: walletId,
          store: storeId,
          organizationId,
          date: new Date(),
          details: `Test income ${timestamp}`,
        };

        log(`Creating income transaction with value: ${transactionData.value}`);
        createdTransactionRef = await saveNewTransaction(db, transactionData);

        log('Waiting 5000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 5000));

        // Compare
        const comparison = await compareViaApi(createdTransactionRef.id, transactionData, transactionData);
        result.comparisonResult = comparison;
        result.passed = comparison.matches;

        // Cleanup - delete the transaction
        log('Cleaning up test transaction...');
        await deleteTransaction(db, createdTransactionRef, transactionData);
        await new Promise(resolve => setTimeout(resolve, 2000));

        break;
      }

      case 'T2': {
        // Create expense transaction
        transactionData = {
          type: 'expense',
          value: 500 + (timestamp % 500),
          sourceWallet: walletId,
          store: storeId,
          organizationId,
          date: new Date(),
          details: `Test expense ${timestamp}`,
        };

        log(`Creating expense transaction with value: ${transactionData.value}`);
        createdTransactionRef = await saveNewTransaction(db, transactionData);

        log('Waiting 5000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 5000));

        // Compare
        const comparison = await compareViaApi(createdTransactionRef.id, transactionData, transactionData);
        result.comparisonResult = comparison;
        result.passed = comparison.matches;

        // Cleanup
        log('Cleaning up test transaction...');
        await deleteTransaction(db, createdTransactionRef, transactionData);
        await new Promise(resolve => setTimeout(resolve, 2000));

        break;
      }

      case 'T3': {
        // Create then delete transaction
        transactionData = {
          type: 'income',
          value: 750 + (timestamp % 250),
          sourceWallet: walletId,
          store: storeId,
          organizationId,
          date: new Date(),
          details: `Test delete ${timestamp}`,
        };

        log(`Creating transaction to delete, value: ${transactionData.value}`);
        createdTransactionRef = await saveNewTransaction(db, transactionData);

        log('Waiting 5000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 5000));

        // Verify it exists first
        const createCheck = await compareViaApi(createdTransactionRef.id, transactionData, transactionData);
        if (!createCheck.foundInSupabase) {
          result.error = 'Transaction was not created in Supabase';
          break;
        }

        // Now delete
        log('Deleting transaction...');
        await deleteTransaction(db, createdTransactionRef, transactionData);

        log('Waiting 5000ms for delete sync...');
        await new Promise(resolve => setTimeout(resolve, 5000));

        // Check if deleted
        const deleted = await checkDeleted(createdTransactionRef.id);
        result.passed = deleted;
        result.comparisonResult = {
          transactionId: createdTransactionRef.id,
          foundInSupabase: !deleted,
          matches: deleted,
          differences: deleted ? [] : [{
            field: 'deleted',
            supabaseValue: 'still exists',
            firestoreValue: 'deleted',
            severity: 'critical',
          }],
        };

        break;
      }

      default:
        result.error = `Unknown variation: ${variationId}`;
    }
  } catch (error) {
    result.error = error.message;
    log(`Error: ${error.message}`);

    // Cleanup on error
    if (createdTransactionRef) {
      try {
        await deleteTransaction(db, createdTransactionRef, transactionData);
      } catch (cleanupError) {
        log(`Cleanup failed: ${cleanupError.message}`);
      }
    }
  }

  return result;
}

/**
 * Run all transaction tests
 */
export async function runFullTransactionTest(log = console.log, selectedVariations = null) {
  log('Starting transaction dual-write tests...');

  const testData = getTransactionTestData();
  if (!testData) {
    log('ERROR: Could not get test data. Make sure you are logged in.');
    return { passed: 0, failed: 0, skipped: 0, results: [] };
  }

  log('\n========================================');
  log('TRANSACTIONS DUAL-WRITE TEST SUITE');
  log('========================================');
  log(`Organization: ${testData.organization.name || testData.organizationId}`);
  log(`Store: ${testData.storeName} (${testData.storeId})`);
  log(`Wallet: ${testData.walletId}`);
  log('========================================\n');

  const variationsToRun = selectedVariations || TRANSACTION_TEST_VARIATIONS.map(v => v.id);
  const results = [];
  let passed = 0;
  let failed = 0;
  let skipped = 0;

  for (const variationId of variationsToRun) {
    const variation = TRANSACTION_TEST_VARIATIONS.find(v => v.id === variationId);
    if (!variation) {
      log(`Skipping unknown variation: ${variationId}`);
      skipped++;
      continue;
    }

    log(`\n--- Running test ${variationId}: ${variation.name} ---`);

    const result = await runSingleTest(variationId, testData, log);
    results.push(result);

    if (result.error) {
      log(`ERROR: ${result.error}`);
      failed++;
    } else if (result.passed) {
      log(`PASS: ${variation.name}`);
      passed++;
    } else {
      log(`FAIL: ${variation.name}`);
      if (result.comparisonResult?.differences?.length > 0) {
        log('Differences: ' + JSON.stringify(result.comparisonResult.differences, null, 2));
      }
      failed++;
    }
  }

  log('\n========================================');
  log('TEST SUMMARY');
  log('========================================');
  log(`Passed: ${passed}`);
  log(`Failed: ${failed}`);
  log(`Skipped: ${skipped}`);
  log('========================================');

  if (failed > 0) {
    log('\nFAILED TESTS:');
    results.filter(r => !r.passed && !r.error).forEach(r => {
      const variation = TRANSACTION_TEST_VARIATIONS.find(v => v.id === r.variationId);
      log(`  - ${r.variationId}: ${variation?.name}`);
      r.comparisonResult?.differences?.forEach(d => {
        log(`    ${d.field}: Supabase="${d.supabaseValue}" vs Firestore="${d.firestoreValue}"`);
      });
    });
  }

  return { passed, failed, skipped, results };
}

export default {
  TRANSACTION_TEST_VARIATIONS,
  getTransactionTestCategories,
  getTransactionTestData,
  runFullTransactionTest,
};
